/* @ngInject */
export default function ReportsService(
  $sails,
  $q,
  $state,
  $location,
  $document,
  EquipmentInfoService,
  $log,
  $http,
  appConfig
) {
  const service = this;

  /* API calls */
  service.createPdf = createPdf;
  service.getFullListOfEquipment = getFullListOfEquipment;
  service.getListOfTanks = getListOfTanks;
  service.getListOfFlowMeters = getListOfFlowMeters;
  service.getEquipmentEventData = getEquipmentEventData;
  service.getProductionReportData = getProductionReportData;
  service.getTotalFlowProductionReportData = getTotalFlowProductionReportData;
  service.getEventsReportData = getEventsReportData;
  service.reportSize = reportSize;
  service.updateEvent = updateEvent;
  service.saveHaulAway = saveHaulAway;

  /* Helper Methods */
  service.invalidDate = invalidDate;
  service.lengthOf = lengthOf;
  service.deepLink = deepLink;
  service.exportData = exportData;
  service.getDisabledReports = reportsToDisable;


  function createPdf(pdfOptions) {
    // $sails doesn't contain functionality to deal with response containing an attachment so must use $http
    return $http.post(`${appConfig.apiBaseUrl}/reports/pdf`, pdfOptions, { responseType: 'arraybuffer' })
      .catch((error) => {
        $log.error('Error: ', error);
        return $q.reject(error.data);
      });
  }

  function getFullListOfEquipment() {
    // this URL will need to be updated when we switch from equipmentTypeIDs
    // to strings for equipment types
    return EquipmentInfoService.getEquipmentByType('tanks')
      .then(tanks =>
        EquipmentInfoService.getEquipmentByType('totalFlowMeters')
          .then((totalFlows, equipments = []) => [...equipments, ...tanks, ...totalFlows]));
  }

  function getListOfTanks() {
    return EquipmentInfoService.getEquipmentByType('tanks');
  }

  function getListOfFlowMeters() {
    return EquipmentInfoService.getEquipmentByType(['totalFlowMeters', 'sensors']);
  }

  function getEquipmentEventData(equipmentId, equipmentType, startDate, endDate, sort, page, count) {
    let url = `/reports/diary/${equipmentId
    }?equipmentType=${equipmentType}&startDate=${startDate}`;
    url += `&endDate=${endDate}&page=${page}&count=${count}${sort ? `&sort=${sort}` : ''}`;
    return $sails.get(url)
      .catch((error) => {
        $log.error('Error: ', error);
        return $q.reject(error.data);
      });
  }

  function getProductionReportData(equipmentIDs, startDate, endDate) {
    const equipmentIDsString = equipmentIDs.join('|');
    return $sails.get(`/reports/production/${equipmentIDsString
    }?startDate=${startDate}&endDate=${endDate}`)
      .catch((error) => {
        $log.error('Error: ', error);
        return $q.reject(error.data);
      });
  }

  function getTotalFlowProductionReportData(tankContractHours, startDate, endDate, equipmentToPossibleDataPoints) {
    const postData = {
      tankContractHours,
      startDate,
      endDate,
      equipmentToPossibleDataPoints
    };
    return $sails.post('/reports/production-totalflow/', postData)
      .catch((error) => {
        $log.error('Error: ', error);
        return $q.reject(error.data);
      });
  }

  function getEventsReportData(
    dashboardIDs,
    eventTypes,
    startDate,
    endDate,
    page,
    count,
    sort,
    filter,
    includeDeleted,
    expData,
    exportType
  ) {

    const dashboardIDsString = dashboardIDs.join('|');
    const eventTypesString = eventTypes.join('|');
    const config = {};
    let url = '/reports/events/?';
    if (expData) {
      config.responseType = 'arraybuffer';
      url = '/reports/export/?';
    }
    url += dashboardIDsString ? `&dashboardIds=${dashboardIDsString}` : '';
    url += startDate ? `&startDate=${startDate}` : '';
    url += endDate ? `&endDate=${endDate}` : '';
    url += eventTypesString ? `&eventTypes=${eventTypesString}` : '';
    url += page ? `&page=${page}` : '';
    url += count ? `&count=${count}` : '';
    url += sort ? `&sort=${sort}` : '';
    url += filter ? `&filter=${filter}` : '';
    url += includeDeleted ? `&includeDeleted=${includeDeleted}` : '';
    url += exportType ? `&exportType=${exportType}` : '';

    const daGet = expData ? $http.get(`${appConfig.apiBaseUrl}${url}`, config) : $sails.get(url, config);

    return daGet
      .catch((error) => {
        $log.error('Error: ', error);
        return $q.reject(error.data);
      });
  }

  function reportSize(dashboardIDs, eventTypes, startDate, endDate, filter, includeDeleted) {
    const dashboardIDsString = dashboardIDs.join('|');
    const eventTypesString = eventTypes.join('|');

    let url = '/reports/reportSize/?';
    url += dashboardIDsString ? `&dashboardIds=${dashboardIDsString}` : '';
    url += startDate ? `&startDate=${startDate}` : '';
    url += endDate ? `&endDate=${endDate}` : '';
    url += eventTypesString ? `&eventTypes=${eventTypesString}` : '';
    url += filter ? `&filter=${filter}` : '';
    url += includeDeleted ? `&includeDeleted=${includeDeleted}` : '';

    return $sails.get(url)
      .catch((error) => {
        $log.error('Error: ', error);
        return $q.reject(error.data);
      });
  }

  function updateEvent(eventId, updatedEventFields) {
    return $sails.put(`/event/${eventId}`, updatedEventFields)
      .catch((error) => {
        $log.error('Error: ', error);
        return $q.reject(error.data);
      });
  }

  function saveHaulAway(haulAwayEvent) {
    return $sails.post('/reports/createHaulAway', haulAwayEvent)
      .catch((error) => {
        $log.error('Error: ', error);
        return $q.reject(error.data);
      });
  }

  function invalidDate(date) {
    const { start, end } = date;
    return !start || !end || start.valueOf() > end.valueOf();
  }

  function lengthOf(object) {
    return !_.isEmpty(object);
  }

  /**
   * Responsible for building the full url linking back to a specific instance of the table.
   * Is used by all four report pages, hence the if statements.
   *
   * @param {object} options - the object coming from the currently loaded child
   * @param {string?} selectedDashboard - the dashboard selected for the graph
   * @param {?Array} selEvTypes - event types selected and stored on the event component
   * @returns {string} the full url
   */
  function deepLink(options, selectedDashboard, selEvTypes) {
    const link = $location.absUrl();
    const newParams = [{
      key: 'start',
      value: options.date.start.valueOf()
    }, {
      key: 'end',
      value: options.date.end.valueOf()
    }];

    if ($state.params.type === 'diary' && options.selectedEquipment) {
      newParams.unshift({
        key: 'equipment',
        value: options.selectedEquipment.id
      });
    }
    if (options.tanks && options.tanks.length) {
      newParams.unshift({
        key: 'tanks',
        value: _.map(options.tanks, 'id').join('|')
      });
    }
    if ($state.params.type === 'events') {
      if (!_.isEmpty(selEvTypes)) {
        newParams.push({
          key: 'eventTypes',
          value: selEvTypes.join('|')
        });
      }
      if (selectedDashboard) {
        newParams.unshift({
          key: 'dashboard',
          value: selectedDashboard
        });
      }
      _.forEach(['page', 'count', 'sort', 'filter'], (key) => {
        if (_.has(options, key)) {
          newParams.push({
            key,
            value: _.get(options, key)
          });
        }
      });
    }
    return `${link}?${_.map(newParams, param => `${param.key}=${param.value}`).join('&')}`;
  }

  function exportData(elem) {
    const innerHTML = $document[0].getElementById(elem).innerHTML;
    const slicedHTML = innerHTML
      .slice(0, innerHTML.indexOf('</table><div ng-table-pagination="params" template-url="templates.pagination"'));
    const blob = new Blob([slicedHTML], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=utf-8'
    });
    saveAs(blob, 'Report.xls');
  }

  /**
   * @returns {string[]} Report pages that should be hidden in nav (based on available equipment types/settings)
   */
  async function reportsToDisable() {
    const equipment = {
      tank: await EquipmentInfoService.getEquipmentByType('tanks'),
      flow: [
        ...await EquipmentInfoService.getEquipmentByType('totalFlowMeters'),
        // The "Production - Flow Meter" report looks for specific sensors equipment in addition to totalFlowMeters
        ..._.filter(
          await EquipmentInfoService.getEquipmentByType('sensors'),
          sensor => _.get(sensor, ['settings', 'flowMeterProductionReport', 'enabled'])
        )
      ]
    };
    return _.reduce(equipment, (toDisable, eq, type) => eq.length ? toDisable : [...toDisable, type], []);
  }
}
